// >>> HER CORRECTION (2026-08-19) — MY ROOM SHOWS THE READING THAT HAS A CLAIM ON THIS MEMBER, AND
// NOTHING ELSE. <<<
//
// HER VERDICT, FROM LIVE USE: *"the list of mini readings at the top of My Room is overcrowding the
// page AND the taps do nothing, both failures. Remove the ribbon entirely from My Room."*
//
// HER REPLACEMENT LAW, VERBATIM: *"My Room shows ONLY the reading(s) with a real claim on that
// member: the funnel/home-card reading they actually clicked to arrive (the continuity token already
// carries which one, their first possession per the standing law), rendered as its beautiful door in
// their room, gated at the tap per FREE-1. No roster, no crowd, nothing unclicked."*
//
// ================================================================================================
// >>> THE TAPS REALLY DID NOTHING, AND THE CAUSE IS STRUCTURAL RATHER THAN A RACE (R25/R180). <<<
//
// Measured against the code before a line was written. `MyRoomReadingDoors` ran
// `onClick={() => onOpen(c.reading)}` and **NAVIGATED NOWHERE.** `openMiniReading` sets a
// module-scope request flag and dispatches an event — and the listener for that event lives on the
// ASTROLOGY page, which the member is not standing on. So the flag was set on My Room, nothing
// listened, nothing moved, and the deadline below dropped it a moment later. **Every tap on every
// one of those doors was dead for every member since the day they shipped**, with nothing red
// anywhere: the button depresses, the page does not change, and there is no error state for a
// navigation that was never asked for.
//
// TRAP-1a's RIBBON did navigate (`openMiniReading(key); goTo(RIBBON_PAGE)`), which is why one of the
// two surfaces worked and the other did not — and it is why *"the taps do nothing"* is a report
// about the crowd rather than about the strip. **So the door NAVIGATES here, and the navigation is
// part of the law rather than a line a call site remembers** (R28/R29: the two surfaces held two
// answers to *what a tap does*, and the copy that was missing is the one that mattered).
// ================================================================================================
// >>> AND THE CROWD WAS NINE DOORS, NOT SIX, WHICH IS HER *OVERCROWDING* MEASURED. <<<
//
// `READING_CARDS` is the UNION of her desktop five and her phone six deduped on the reading — NINE
// promises — and `MyRoomReadingDoors` drew every one of them in a grid above her room. Her law
// replaces that roster with the member's OWN arrival, so the count is ZERO or ONE and never nine.
// ================================================================================================
// >>> ONE SURFACE, BOTH AUDIENCES, AND THAT IS HER LAW READ EXACTLY (R18 COLLAPSED ON PURPOSE). <<<
//
// The two retired surfaces split on payment: the ribbon drew only for a member who had NOT paid
// (`ribbonState` answered `hide` for a member) and the doors drew only for one who HAD. Her clause
// names one behaviour for both — *"taps land true (the popup-to-membership-page flow for unpaid, the
// reading for paid)"* — so the DOOR is the same door for everybody and the difference is what
// happens at the tap, which is FREE-1's own ruling: no wall on browsing, the gate at the generation
// moment. A surface that drew for one audience and not the other would have to ask a question this
// law has no business asking.
// ================================================================================================
// >>> WHAT AN ORIGIN IS, AND WHY IT IS TWO SOURCES RATHER THAN ONE. <<<
//
// Her sentence says *"the continuity token already carries which one"*. **Half of that is true and
// the half that is not is the round.** A FUNNEL arrival really does carry it — `funnel_leads.funnel`
// is written at capture, `prefillFromLead` hands it across the bridge, and `converted_user_id` links
// the row to the member. A HOME-CARD arrival carries NOTHING: HOME-1's ruling is that all her cards
// land on SIGNUP, `openSignup(intent, next)` takes no card, and her own worked example
// (*"Were you born to be wealthy?"*) is a home card. So the card key is carried from here on, and
// the gap is REPORTED rather than papered over (R19/R141: a receipt that claims a carrier the code
// does not have costs the next window exactly what a false finding about the code costs).
//
// **A CARD OUTRANKS A FUNNEL WHEN BOTH ARE PRESENT, AND THE ORDER IS THE RULING (R18).** A visitor
// who came through an advertisement weeks ago and then tapped a card on the home page today has
// clicked TWO things; the card is the one she is standing in front of, and the funnel's own reading
// has already been DELIVERED to her shelf as her first possession (L11), so it is not lost by
// yielding here. The other order hands her a door for the thing she did not just click.
// ================================================================================================
// >>> IT IMPORTS ONLY IMPORT-FREE ROSTERS, WHICH IS THE PROPERTY THE GATE RESTS ON (R146). <<<
//
// `homeTapMap.js` and `funnels.js` each import NOTHING, so this whole chain loads inside a `.mjs`
// fixture with no bundler, no browser and no database — which is what lets every clause below be
// RUN rather than read out of a component (R8/R95: a law inside a TSX file is a law no gate can
// call). The door mechanism moved here VERBATIM from the retired reading-ribbon law, which is DELETED: a
// module named after a component it no longer holds is a stale receipt one level up, and this house
// has already renamed `FloatingCloset.tsx` for exactly that reason (R165).
// ================================================================================================
import { READING_CARDS, READING_CARDS_BUILT } from "./homeTapMap.js";
import { FUNNEL_KEYS } from "./funnels.js";

/**
 * >>> THE QUERY KEY A HOME CARD CARRIES TO THE SIGNUP PAGE. <<<
 *
 * One declaration, because the home page WRITES it and the signup page READS it, and two spellings
 * of *which card sent her here* is an origin that silently resolves to nothing — the failure with no
 * symptom at all, since a member with no origin is a legal state (her organic clause) and renders as
 * a perfectly clean room (R28/R29).
 */
export const ORIGIN_PARAM = "card";

/**
 * >>> WHICH READING EACH OF HER THREE ADVERTISEMENTS IS ABOUT. <<<
 *
 * Her funnel titles and these mini readings are the same question in her own words:
 * *"Were You Born to Be Wealthy?"*, *"How Psychic Are You?"*, *"See Your Wealth Timeline"*. The map
 * is DECLARED rather than inferred from the copy, because a title is a spelling and a reading key is
 * the identity (R41) — and it is held as a BICONDITIONAL against `FUNNEL_KEYS` by the fixture, so a
 * fourth advertisement of hers joins by going RED rather than by resolving to nothing.
 */
export const FUNNEL_ORIGIN_READING = Object.freeze({
  wealthy: "born-to-be-wealthy",
  psychic: "how-psychic-are-you",
  timeline: "when-is-money-coming",
});

/** Every reading key that may ever be an origin: her cards, and the three her funnels are about. */
export function originReadingKeys() {
  const keys = new Set();
  for (const c of READING_CARDS) keys.add(c.reading);
  for (const k of FUNNEL_KEYS) {
    const r = FUNNEL_ORIGIN_READING[k];
    if (r) keys.add(r);
  }
  return [...keys];
}

/**
 * >>> AN ORIGIN MUST BE A READING THIS HOUSE CAN ACTUALLY DELIVER. <<<
 *
 * `READING_CARDS_BUILT` is HOME-1's own roster of the promises with a member reading behind them.
 * A card she clicked whose reading is not built yet would put a beautiful door in her room that
 * opens onto nothing — which is A2's dead box wearing her own artwork, and it is strictly worse than
 * the clean room her organic clause asks for. **So an unbuilt origin answers NOTHING**, and the
 * fixture drives that case, because the honest failure is a room with one fewer door and the
 * dishonest one is a door that cannot open (R19).
 */
export function readingCanBeOrigin(reading, built = READING_CARDS_BUILT) {
  const r = typeof reading === "string" ? reading.trim() : "";
  if (!r) return false;
  // `built` is a PARAMETER because EVERY card is built today, so a clause driven only over the live
  // roster measures its own alphabet (R129/R135) -- the mutation run reported exactly that. The
  // default is the real roster, so every caller is byte-identical (ITEM 12's neutrality argument).
  return originReadingKeys().includes(r) && built.includes(r);
}

/**
 * The reading a HOME CARD names. The parameter carries the READING key rather than the card id,
 * because two of her cards ask the same reading in two wordings and the id is a fact about which
 * ARTWORK she was looking at — which is not what her room owes her (R41).
 */
export function originFromCard(raw) {
  const key = typeof raw === "string" ? raw.trim() : "";
  return readingCanBeOrigin(key) ? key : "";
}

/** The reading a FUNNEL arrival is about. An unrostered funnel answers nothing rather than
 *  `FUNNEL_FALLBACK`'s wealthy: that fallback exists so a paid click always sees a real landing
 *  page, and borrowing it here would put a door in the room of somebody who never clicked it. */
export function originFromFunnel(raw) {
  const key = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (!FUNNEL_KEYS.includes(key)) return "";
  const reading = FUNNEL_ORIGIN_READING[key] || "";
  return readingCanBeOrigin(reading) ? reading : "";
}

/**
 * >>> THE ONE RESOLVER, READ BY THE SIGNUP DOOR AND BY NOBODY ELSE. <<<
 *
 * Both sources in, one reading key out, `""` for a member who clicked neither. Every uncertainty
 * answers `""` — a value that is not a string, a key nobody rosters, a reading with no engine — and
 * that direction is the ruling (R19/R8-4): a wrong door is a promise this house did not make, and
 * an absent door is the clean room her own organic clause describes.
 */
export function resolveOrigin({ card = "", funnel = "" } = {}) {
  return originFromCard(card) || originFromFunnel(funnel) || "";
}

/**
 * >>> WHAT THE DOOR SAYS, AND IT IS HER OWN PROMISE VERBATIM OFF THE ROSTER. <<<
 *
 * `READING_CARDS` keeps the DESKTOP wording where two cards ask one reading, which is HOME-1's own
 * dedupe ruling (the newer artwork wins). A paraphrase composed here would be a different promise
 * from the one she clicked, in the room she was brought to by clicking it.
 */
export function originDoor(reading) {
  const r = typeof reading === "string" ? reading.trim() : "";
  if (!readingCanBeOrigin(r)) return null;
  const card = READING_CARDS.find((c) => c.reading === r);
  if (!card) return null;
  return { reading: r, promise: card.promise };
}

/**
 * >>> THREE STATES, AND *WE HAVE NOT LOOKED YET* IS NOT *SHE CLICKED NOTHING* (R18/L5.1). <<<
 *
 * `wait` — the room has not been told what this member's origin is, so it draws NOTHING. Collapsing
 * this into `none` is harmless-looking and is the flicker this house has paid for on the membership
 * box, on the home page and in the Living Room; collapsing it into `show` draws a door for a reading
 * nobody has claimed.
 * `none` — she arrived organically, and her room is the standard doors and nothing else. **That is
 * her own verify clause and it is a legal, common state**, so it is a NAMED answer rather than the
 * absence of one.
 * `show` — one door, hers.
 *
 * `known` is presence and never truthiness: it arrives off a payload, where `Boolean("false")` is
 * `true` (R53/R136/R137), and a coerced answer here paints a door at a member who has not been asked
 * about yet.
 */
export function roomDoorState({ known = false, origin = "" } = {}) {
  if (known !== true) return "wait";
  return originDoor(origin) ? "show" : "none";
}

// ================================================================================================
// >>> THE DOOR MECHANISM — MOVED VERBATIM FROM THE RETIRED RIBBON LAW, WHICH IS DELETED. <<<
//
// A mini reading is a MODAL inside the astrology page rather than a route, so a bare `goTo` lands
// her in the room with nothing open — which is half the tap done, and is exactly what her report is
// about one surface over. The request is therefore ANNOUNCED (she may already be standing there) AND
// left as a FLAG (the navigation REMOUNTS the page, so the event fires before the listener exists).
// `CauldronDoor`'s own shape, and this house has paid for that race twice.
// ================================================================================================
export const OPEN_MINI_READING_EVENT = "twelve:open-mini-reading";
export const MINI_READING_PAGE = "astrology";

/**
 * >>> THE DROP IS A BAND, AND THE 2000ms IT REPLACES WAS RACING A NETWORK FETCH. <<<
 *
 * The consumer cannot fire until `miniConfigs` has ARRIVED — the astrology room fetches its own
 * roster — so the deadline is measured against a request that has to survive a navigation AND a
 * round trip on a cold container. At 2000ms a phone on cellular, or a Railway container waking up,
 * dropped the request before the roster landed and the reading never opened, silently. **That is the
 * second half of *the taps do nothing*, and it is the half that only fails sometimes**, which is why
 * it survived: on a warm laptop it works every time.
 *
 * It is HELD AT BOTH ENDS by the fixture, because a ceiling alone is satisfied by a request that
 * never expires — and what expiry is FOR is real: the flag is module state, so it dies with the page,
 * but an SPA session where she taps the door and then wanders somewhere else entirely must not have
 * a paid door open itself when she later walks into the astrology room. Long enough for the journey,
 * short enough that it is not a journey any more.
 */
export const MINI_REQUEST_MS = 15000;
export const MINI_REQUEST_FLOOR_MS = 8000;
export const MINI_REQUEST_CEILING_MS = 60000;

let requested = null;
let dropAt = 0;

/**
 * Take the pending request, if there is one. Reading it CONSUMES it, so it cannot fire twice.
 *
 * >>> THE DROP IS A DEADLINE READ HERE RATHER THAN A TIMER THAT FIRES. <<< A `setTimeout` is the
 * obvious build and it has a real bug in it: a SECOND tap inside the window leaves the FIRST tap's
 * timer running, and that timer clears the NEWER request — so a member who taps twice quickly lands
 * in the room with nothing open, which is exactly the half-done tap this door exists to prevent.
 * A deadline compared at read time cannot do that, and it needs no clock injected to be RUN.
 */
export function consumeMiniRequest(now = Date.now()) {
  // The expiry test is what makes a stale request answer NOTHING rather than opening a paid door on
  // a later visit. It is read through `miniRequestPending` so the two can never disagree about
  // whether a request is still live (R28/R29) -- which is the whole shape of this drop.
  const v = miniRequestPending(now);
  requested = null;
  dropAt = 0;
  return v;
}

export function miniRequestPending(now = Date.now()) {
  if (requested && now > dropAt) return null;
  return requested;
}

/**
 * Ask the astrology room to open one reading.
 *
 * `win` and `now` are PARAMETERS so a `.mjs` gate RUNS this against a fake window and reads back
 * what it dispatched and flagged — including the DROP, which has no visible symptom (R46's
 * locked-door shape, and R26's own reason for taking it).
 *
 * **AN EMPTY KEY DISPATCHES NOTHING AND FLAGS NOTHING**, or a door whose origin arrived malformed
 * navigates her into the room and opens a reading that does not exist.
 */
export function openMiniReading(key, { win = typeof window === "undefined" ? null : window, now = Date.now() } = {}) {
  const k = typeof key === "string" ? key.trim() : "";
  if (!k) return false;
  requested = k;
  dropAt = now + MINI_REQUEST_MS;
  if (win) win.dispatchEvent(new win.CustomEvent(OPEN_MINI_READING_EVENT, { detail: { key: k } }));
  return true;
}

// ================================================================================================
// >>> THE CARD SURVIVING THE JOURNEY, AND IT IS `sessionStorage` FOR L10's OWN RECORDED REASON. <<<
//
// The tap is on the HOME page and the write is at ROOM-CREATE, and between them sits a signup form
// and — for most members — a full page navigation out to Google and back. So the card cannot live in
// React state, and `localStorage` is refused for the reason the funnel pre-fill refuses it one
// module over: it would pour a stranger's origin into the room of whoever uses that browser next
// week. A session dies with the tab, which is exactly the life of an arrival.
//
// `store` is a PARAMETER so a gate RUNS this (`readDismissed`'s shape). A storage that THROWS —
// Safari in private mode — reads as NOTHING STASHED, which is the safe direction: the worst case is
// a clean room, which is a legal state her own organic clause describes.
// ================================================================================================
export const ORIGIN_STASH_KEY = "twelve_house_room_origin";

export function writeStashedOrigin(store, reading) {
  try {
    // Only a real origin is ever kept. The roster test happens HERE as well as at the door, so a
    // stash cannot hold a key the server would refuse and cannot outlive a reading being retired.
    const r = originFromCard(reading);
    if (!r) { store?.removeItem(ORIGIN_STASH_KEY); return; }
    store?.setItem(ORIGIN_STASH_KEY, r);
  } catch {
    /* a storage that refuses is not a reason to lose the room */
  }
}

export function readStashedOrigin(store) {
  try {
    return originFromCard(store?.getItem(ORIGIN_STASH_KEY));
  } catch {
    return "";
  }
}

export function clearStashedOrigin(store) {
  try {
    store?.removeItem(ORIGIN_STASH_KEY);
  } catch {
    /* nothing to do; it dies with the tab either way */
  }
}
